// AdminPriceTool — admin sub-view, extracted verbatim from App.js.
import { useState, useEffect, useMemo, useRef } from "react";
import { C } from "../lib/theme";
import { fmt } from "../lib/format";
import { Card, EmptyState, SectionHdr } from "../components/ui";
import { CsvImportExport } from "../components/CsvImportExport";
import { pick } from "../lib/csv";
import { calcItem } from "../lib/calc";
import { upsertMenuItem, deleteMenuItem } from "../data/menuItems";
import { useT } from "../lib/i18n";
import { isOverrideActive } from "../lib/accountAccess";

// CSV columns for the Price Tool. ID first so an exported file round-trips:
// re-importing it updates existing items (matched by id) instead of duplicating.
const MENU_CSV_HEADERS = ["ID", "Platform", "Item Name", "Cost", "Selling Price", "Discount %", "Discount Cap", "Campaign Fee"];

export function AdminPriceTool({ws, tiers, cfg, setWorkspaces, chId, wsId, account, onShowUpgrade, refreshWorkspaces, addTick}) {
  const { t } = useT();
  const [adding,setAdding]=useState(false);
  const [editId,setEditId]=useState(null);
  const [saving,setSaving]=useState(false);
  const [form,setForm]=useState({channelId:"",name:"",cost:"",price:"",disc:"0",fee:"0",cap:"0"});
  const [err,setErr]=useState("");
  const channels = ws?.channels ? Object.values(ws.channels) : [];
  const filterLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : t("pricetool.allPlatforms");

  // Price Tool is platform-specific — filter items to selected channel
  const allItems = useMemo(()=>{
    const sources = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : channels;
    return sources.flatMap(c=>(c.menu||[]).map(m=>({...m,channelId:c.id,platform:c.platform,color:c.color,commission:c.commission,cardFeePct:c.cardFeePct})));
  },[ws,chId,channels]);

  // Trial restriction: trial users get 5 prices per workspace.
  const isTrialing = account?.subscription_status === "trialing";
  const isFreeOverride = isOverrideActive(account);
  const trialPriceLimit = isTrialing && !isFreeOverride ? 5 : Infinity;
  const currentPriceCount = (ws?.channels ? Object.values(ws.channels) : []).reduce((sum, c) => sum + ((c.menu||[]).length), 0);
  const atTrialPriceLimit = currentPriceCount >= trialPriceLimit;

  function openAdd(){
    if (atTrialPriceLimit) {
      // Block the add UI; show upgrade modal instead
      if (onShowUpgrade) onShowUpgrade();
      return;
    }
    const defaultCh = chId || channels[0]?.id || "";
    setForm({channelId:defaultCh,name:"",cost:"",price:"",disc:"0",fee:"0",cap:"0"});
    setEditId(null); setAdding(true); setErr("");
  }

  // "+ Add menu item" lives in the pill-bar toolbar (AdminView); it bumps addTick.
  const addTickRef = useRef(addTick);
  useEffect(()=>{
    if(addTick !== addTickRef.current){
      addTickRef.current = addTick;
      openAdd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[addTick]);
  function openEdit(item){
    setForm({channelId:item.channelId,name:item.name,cost:String(item.cost),price:String(item.price),disc:String((item.disc||0)*100),fee:String(item.fee||0),cap:String(item.cap||0)});
    setEditId(item.id); setAdding(true); setErr("");
  }

  async function saveItem(){
    if(!form.channelId||!form.name||!form.price){setErr(t("pricetool.err.required"));return;}
    setSaving(true);
    try {
      const payload = {
        workspace_id: ws.id,
        channel_id: form.channelId,
        name: form.name,
        cost_price: parseFloat(form.cost)||0,
        selling_price: parseFloat(form.price)||0,
        discount_pct: (parseFloat(form.disc)||0)/100,
        campaign_fee: parseFloat(form.fee)||0,
        discount_cap: parseFloat(form.cap)||0,
      };
      if(editId) payload.id = editId;
      const { data, error } = await upsertMenuItem(payload);
      if(error){setErr(error.message);setSaving(false);return;}

      // Update local workspaces state instead of reloading the page
      const newItem = {
        id: data.id,
        name: data.name,
        cost: parseFloat(data.cost_price) || 0,
        price: parseFloat(data.selling_price) || 0,
        disc: parseFloat(data.discount_pct) || 0,
        fee: parseFloat(data.campaign_fee) || 0,
        cap: parseFloat(data.discount_cap) || 0,
      };
      if (setWorkspaces && wsId) {
        setWorkspaces(prev => {
          const next = {...prev};
          const w = {...next[wsId]};
          w.channels = {...w.channels};
          const ch = {...w.channels[form.channelId]};
          const existingMenu = ch.menu || [];
          ch.menu = editId
            ? existingMenu.map(m => m.id === editId ? newItem : m)
            : [...existingMenu, newItem];
          w.channels[form.channelId] = ch;
          next[wsId] = w;
          return next;
        });
      }

      setAdding(false); setEditId(null);

      // Safety net: re-fetch workspaces from DB to ensure UI matches DB state.
      // Local setWorkspaces above SHOULD be enough, but if any state-sync issue
      // hides the new item, this guarantees the table updates.
      if (refreshWorkspaces) {
        setTimeout(() => refreshWorkspaces(), 100);
      }
    } catch(e){ setErr(t("pricetool.err.saveFailed")); }
    setSaving(false);
  }

  async function deleteItem(itemId){
    if(!window.confirm(t("pricetool.confirm.delete"))) return;
    const { error } = await deleteMenuItem(itemId);
    if (error) { alert(t("pricetool.alert.deleteFailed") + error.message); return; }
    // Update local workspaces state
    if (setWorkspaces && wsId) {
      setWorkspaces(prev => {
        const next = {...prev};
        const w = {...next[wsId]};
        w.channels = {...w.channels};
        Object.keys(w.channels).forEach(cid => {
          const ch = {...w.channels[cid]};
          ch.menu = (ch.menu || []).filter(m => m.id !== itemId);
          w.channels[cid] = ch;
        });
        next[wsId] = w;
        return next;
      });
    }
  }

  // ── CSV export / import (deterministic, no AI) ───────────────────────
  // Resolve a channel by its platform name (case-insensitive) for import.
  const channelByPlatform = (name) => {
    const n = String(name||"").trim().toLowerCase();
    return (ws?.channels ? Object.values(ws.channels) : []).find(c => String(c.platform||"").trim().toLowerCase() === n);
  };
  function buildMenuExportRows(){
    return allItems.map(m => [
      m.id || "",
      m.platform || "",
      m.name || "",
      m.cost ?? 0,
      m.price ?? 0,
      +(((m.disc||0)*100).toFixed(4)),
      m.cap || 0,
      m.fee || 0,
    ]);
  }
  async function importMenu(rows){
    let added=0, updated=0; const errors=[];
    for(let i=0;i<rows.length;i++){
      const row=rows[i];
      const rowNo=i+2; // header is row 1
      const platform = pick(row,"Platform","Channel");
      const name = pick(row,"Item Name","Name","Item");
      const priceStr = pick(row,"Selling Price","Price");
      const ch = channelByPlatform(platform);
      if(!ch){ errors.push(`${t("pricetool.import.unknownPlatformPrefix")} ${rowNo}${t("pricetool.import.unknownPlatformMid")} "${platform||"(blank)"}"`); continue; }
      if(!name){ errors.push(`${t("pricetool.import.unknownPlatformPrefix")} ${rowNo}${t("pricetool.import.missingName")}`); continue; }
      if(priceStr==="" || isNaN(parseFloat(priceStr))){ errors.push(`${t("pricetool.import.unknownPlatformPrefix")} ${rowNo} (${name})${t("pricetool.import.missingPriceSuffix")}`); continue; }
      const id = pick(row,"ID","Id");
      const payload = {
        workspace_id: ws.id,
        channel_id: ch.id,
        name,
        cost_price: parseFloat(pick(row,"Cost","Cost Price"))||0,
        selling_price: parseFloat(priceStr)||0,
        discount_pct: (parseFloat(pick(row,"Discount %","Discount","Disc"))||0)/100,
        campaign_fee: parseFloat(pick(row,"Campaign Fee","Fee"))||0,
        discount_cap: parseFloat(pick(row,"Discount Cap","Cap"))||0,
      };
      if(id) payload.id = id;
      const { error } = await upsertMenuItem(payload);
      if(error){ errors.push(`Row ${rowNo} (${name}): ${error.message}`); continue; }
      if(id) updated++; else added++;
    }
    if(refreshWorkspaces) await refreshWorkspaces();
    return { added, updated, skipped: errors.length, errors };
  }

  return (
    <div style={{padding:24}}>
      <SectionHdr title={t("pricetool.title")} sub={`${ws?.name||t("pricetool.workspaceFallback")} · ${filterLabel} · ${allItems.length} ${t("pricetool.itemsSuffix")}`}/>

      {/* Trial limit banner — professional progress indicator */}
      {isTrialing && !isFreeOverride && (() => {
        const remaining = Math.max(0, trialPriceLimit - currentPriceCount);
        const pctUsed = Math.min(100, Math.round((currentPriceCount / trialPriceLimit) * 100));
        return (
          <div style={{
            marginBottom: 18,
            padding: "14px 18px",
            background: "#fff",
            borderRadius: 10,
            border: `1px solid ${atTrialPriceLimit ? "#fbbf24" : "#e2e8f0"}`,
            boxShadow: "0 1px 2px rgba(15,23,42,.04)",
          }}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{
                  fontSize:10,
                  fontWeight:700,
                  letterSpacing:.6,
                  textTransform:"uppercase",
                  color:atTrialPriceLimit ? "#92400e" : "#475569",
                  background:atTrialPriceLimit ? "#fef3c7" : "#f1f5f9",
                  padding:"3px 8px",
                  borderRadius:4,
                }}>
                  {t("pricetool.trial.badge")}
                </span>
                <span style={{fontSize:13,color:"#0f172a",fontWeight:600,letterSpacing:"-.1px"}}>
                  {atTrialPriceLimit
                    ? t("pricetool.trial.limitReached")
                    : `${currentPriceCount} ${t("pricetool.trial.countLabelA")} ${trialPriceLimit} ${t("pricetool.trial.countLabelB")}`}
                </span>
              </div>
              {atTrialPriceLimit && onShowUpgrade && (
                <button onClick={onShowUpgrade} style={{
                  padding:"7px 14px",
                  borderRadius:7,
                  border:"none",
                  background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",
                  color:"#fff",
                  cursor:"pointer",
                  fontSize:12,
                  fontWeight:600,
                  fontFamily:"inherit",
                  letterSpacing:"-.1px",
                  boxShadow:"0 2px 5px rgba(15,23,42,.18)",
                }}>
                  {t("pricetool.trial.upgrade")}
                </button>
              )}
            </div>
            {/* Progress bar */}
            <div style={{height:4,background:"#f1f5f9",borderRadius:2,overflow:"hidden",marginBottom:6}}>
              <div style={{
                height:"100%",
                width:`${pctUsed}%`,
                background:atTrialPriceLimit
                  ? "linear-gradient(90deg, #fbbf24, #f59e0b)"
                  : "linear-gradient(90deg, #3b5bdb, #1e1b4b)",
                transition:"width .3s ease",
              }}/>
            </div>
            <div style={{fontSize:11.5,color:"#64748b",fontWeight:500,letterSpacing:"-.05px"}}>
              {atTrialPriceLimit
                ? t("pricetool.trial.subscribeHint")
                : remaining === 1
                  ? t("pricetool.trial.oneSlotLeft")
                  : `${remaining} ${t("pricetool.trial.slotsLeftSuffix")}`}
            </div>
          </div>
        );
      })()}

      {/* Add/edit form */}
      {adding&&(
        <Card sx={{marginBottom:20,border:`2px solid ${C.blue}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:600,color:C.text}}>{editId?t("pricetool.form.editTitle"):t("pricetool.form.newTitle")}</div>
            <button onClick={()=>{setAdding(false);setEditId(null);}} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{t("pricetool.form.cancel")}</button>
          </div>
          {!!err&&<div style={{padding:"8px 12px",background:C.red10,borderRadius:6,marginBottom:12,fontSize:12,color:C.red}}>{err}</div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.platform")}</label>
              <select value={form.channelId} onChange={e=>setForm({...form,channelId:e.target.value})}
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,color:C.text,background:"#fafafa",fontFamily:"inherit"}}>
                {channels.map(c=>(<option key={c.id} value={c.id}>{c.platform}</option>))}
              </select>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.itemName")}</label>
              <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder={t("pricetool.form.itemNamePlaceholder")}
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.cost")}</label>
              <input value={form.cost} onChange={e=>setForm({...form,cost:e.target.value})} placeholder="3.50" type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.sellingPrice")}</label>
              <input value={form.price} onChange={e=>setForm({...form,price:e.target.value})} placeholder="9.99" type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.discountPct")}</label>
              <input value={form.disc} onChange={e=>setForm({...form,disc:e.target.value})} placeholder="0" type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.campaignFee")}</label>
              <input value={form.fee} onChange={e=>setForm({...form,fee:e.target.value})} placeholder="0" type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{t("pricetool.form.discountCap")}</label>
              <input value={form.cap} onChange={e=>setForm({...form,cap:e.target.value})} placeholder={t("pricetool.form.capPlaceholder")} type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <button onClick={saveItem} disabled={saving}
            style={{padding:"10px 22px",borderRadius:7,border:"none",background:saving?C.muted:C.green,color:"#fff",cursor:saving?"not-allowed":"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>
            {saving?t("pricetool.form.saving"):(editId?t("pricetool.form.updateItem"):t("pricetool.form.saveItem"))}
          </button>
        </Card>
      )}

      {/* Items table — styled to match the Daily Log table for consistency. */}
      <div style={{border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",background:"#fff",boxShadow:C.s2,marginBottom:16}}>
        <div style={{overflowX:"auto",overflowY:"auto",maxHeight:"60vh",WebkitOverflowScrolling:"touch"}}>
          <table style={{borderCollapse:"separate",borderSpacing:0,fontSize:13.5,width:"100%",minWidth:920,fontVariantNumeric:"tabular-nums"}}>
            <thead>
              <tr>
                {[[t("pricetool.col.channel"),"left","channel"],[t("pricetool.col.itemName"),"left","itemName"],[t("pricetool.col.cost"),"right","cost"],[t("pricetool.col.sellingPrice"),"right","sellingPrice"],[t("pricetool.col.discount"),"right","discount"],[t("pricetool.col.campaignFee"),"right","campaignFee"],[t("pricetool.col.profit"),"right","profit"],["","right","actions"]].map(([h,al,k])=>(
                  <th key={k} style={{padding:"16px 18px",textAlign:al,fontWeight:600,color:"#fff",whiteSpace:"nowrap",fontSize:11,letterSpacing:.3,background:C.blue,position:"sticky",top:0,zIndex:1}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allItems.length===0?(
                <tr><td colSpan={8} style={{padding:0}}><EmptyState icon="tag" title={t("pricetool.empty.title")} message={t("pricetool.empty.message")} /></td></tr>
              ):allItems.map((m,i)=>{
                const r=calcItem(m.cost,m.price,m.disc,m.fee,{aggCommission:m.commission||cfg.aggCommission,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true}, m.cardFeePct ?? 0, m.cap||0);
                const zebra = i%2===1 ? C.blue5 : "#fff";
                return(
                  <tr key={m.id} style={{background:zebra,transition:"background .1s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=C.blue10;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=zebra;}}>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,whiteSpace:"nowrap"}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:7,fontWeight:600,color:C.text}}>
                        <span style={{width:8,height:8,borderRadius:3,background:m.color,flexShrink:0}}/>{m.platform}
                      </span>
                    </td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,fontWeight:500,color:C.text,whiteSpace:"nowrap"}}>{m.name}</td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right",color:C.text}}>{fmt(m.cost)}</td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right",color:C.text}}>{fmt(m.price)}</td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right",color:C.sub}}>{((m.disc||0)*100).toFixed(0)}%{m.cap>0?` · ${t("pricetool.cap")} ${fmt(m.cap)}`:""}</td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right",color:C.sub}}>{fmt(m.fee||0)}</td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right"}}><span style={{fontWeight:700,color:(r.profit<0?C.red:C.text)}}>{fmt(r.profit)}</span></td>
                    <td style={{padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,textAlign:"right",whiteSpace:"nowrap"}}>
                      <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                        <button onClick={()=>openEdit(m)} title={t("pricetool.action.editItem")}
                          style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                          onMouseEnter={e=>{e.currentTarget.style.background=C.bg3;e.currentTarget.style.color=C.text;e.currentTarget.style.borderColor=C.muted2;}}
                          onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                        </button>
                        <button onClick={()=>deleteItem(m.id)} title={t("pricetool.action.deleteItem")}
                          style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                          onMouseEnter={e=>{e.currentTarget.style.background=C.red10;e.currentTarget.style.color=C.red;e.currentTarget.style.borderColor="#f8c9c9";}}
                          onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV import / export — deterministic, round-trips by ID. */}
      <CsvImportExport
        title={t("pricetool.csv.title")}
        subtitle={t("pricetool.csv.subtitle")}
        templateHeaders={MENU_CSV_HEADERS}
        buildExportRows={buildMenuExportRows}
        exportFilename={`netrev-menu-${(ws?.name||"export").replace(/\s+/g,"-")}.csv`}
        onImport={importMenu}
        noun="items"
      />
    </div>
  );
}
